import { marginBp } from '@app/shared/price-calc';
import { formatPln } from '../catalog/money';
import { formatDecimal } from '../ui/numbers';

/**
 * What a rental needs to be named on hover (rule 7a: the API leaves out a hidden quantity, supplier, price or cost —
 * a quote line's rentals come only with the costs).
 */
export type RentedPiece = { quantity?: number; supplierName?: string; status?: string; itemId?: string | null; keptByQuote?: boolean; costOnly?: boolean; priceDayGrosze?: number | null; costGrosze?: number };

const STATUS: Record<string, string> = { planowane: 'planowane', potwierdzone: 'potwierdzone', odebrane: 'odebrane', zwrocone: 'zwrócone' };

const marginOf = (cost: number, revenue: number): string => {
  const bp = marginBp(revenue - cost, revenue);
  return bp === null ? '' : ` · marża ${formatDecimal(Math.round(bp / 10) / 10)}%`;
};

/**
 * Owner 2026-10-24 (pkt 7: „wywal to + dopożyczone, a w tym dymku zrób wyszczególnienie ile dopożyczone od kogo, za ile
 * i jaka marża”): the hover of a line rented for the event — one row per supplier (its rentals summed; the status when
 * not just planned) with pieces × price per day = cost, and the margin when the line's net per piece is known (the
 * quote, with the costs); a total row. Without prices or costs (rule 7a) only the pieces. Undefined: nothing to name.
 */
export function rentalsDetail(rentals: readonly RentedPiece[], opts: { netPerPieceGrosze?: number } = {}): string | undefined {
  const shown = rentals.filter((r) => r.quantity !== undefined && r.supplierName);
  if (shown.length === 0) return undefined;
  type Row = { name: string; statuses: Set<string>; quantity: number; prices: Set<number | null>; cost: number | undefined };
  const rows = new Map<string, Row>();
  for (const r of shown) {
    const row = rows.get(r.supplierName!) ?? { name: r.supplierName!, statuses: new Set(), quantity: 0, prices: new Set(), cost: 0 };
    if (r.status && r.status !== 'planowane') row.statuses.add(STATUS[r.status] ?? r.status);
    row.quantity += r.quantity!;
    if (r.priceDayGrosze !== undefined) row.prices.add(r.priceDayGrosze);
    row.cost = row.cost === undefined || r.costGrosze === undefined ? undefined : row.cost + r.costGrosze;
    rows.set(r.supplierName!, row);
  }
  const net = opts.netPerPieceGrosze;
  const lines = [...rows.values()].map((row) => {
    const price = row.prices.size === 1 ? [...row.prices][0] : undefined;
    const status = row.statuses.size > 0 ? ` (${[...row.statuses].join(', ')})` : '';
    const priced = price !== undefined && price !== null ? ` × ${formatPln(price)}/doba` : '';
    const cost = row.cost !== undefined ? `${priced} = ${formatPln(row.cost)}${net !== undefined ? marginOf(row.cost, net * row.quantity) : ''}` : '';
    return `${row.name}${status}: ${formatDecimal(row.quantity)} szt.${cost}`;
  });
  const quantity = [...rows.values()].reduce((s, r) => s + r.quantity, 0);
  const costs = [...rows.values()].map((r) => r.cost);
  const total = costs.every((c) => c !== undefined) ? (costs as number[]).reduce((s, c) => s + c, 0) : undefined;
  const totalRow = `Razem: ${formatDecimal(quantity)} szt.${total !== undefined ? ` = ${formatPln(total)}${net !== undefined ? marginOf(total, net * quantity) : ''}` : ''}`;
  return ['Dopożyczono:', ...lines, totalRow].join('\n');
}

/** The pieces only (kept for callers that name the rentals without amounts). */
export const rentedText = (rentals: readonly RentedPiece[]): string | undefined => rentalsDetail(rentals.map(({ priceDayGrosze: _p, costGrosze: _c, ...r }) => r));

/** The event's rentals of my item (not a quote's whole supplier line, not a cost only), as the packlist's hover says them (no margin: no revenue there). */
export function rentedOfItem(rentals: readonly RentedPiece[], itemId: string): string | undefined {
  return rentalsDetail(rentals.filter((r) => r.itemId === itemId && !r.keptByQuote && !r.costOnly));
}
